import { Component } from "@omnia/shared/shell/component"
import { defineComponent, selectMany } from "../../../omnia"
import { getProductUnitsTextNormalized } from "../../../services/units"
import { INutritionAppRate, INutritionBiomassOptions, INutritionService } from "../../../types"
import { CroppingEditVm } from "../cropping/croppingEditVm"
import { CroppingGroup } from "../cropping/croppingGroup"
import { PlanEditContext } from "../editContext"
import { ProductApplicationVm } from "../products/productApplicationVm"
import { IEditPlanCroppingArguments } from "../types"
import { calculateCroppingAppRateManual } from "./calc"
import { ProductAppInput } from "@omnia/shared/generated/enums"


export class BiomassEditCtrl extends Component {

    static create(): ng.IDirective {

        return defineComponent(BiomassEditCtrl, "app/nutrition/plan/edit/biomass/biomass.html")

    }

    public readonly readOnly = this.svc.readOnly

    private ctxt: PlanEditContext
    private groups: CroppingGroup[]

    public showApproxWarning: boolean

    static $inject = ["$scope", "OmniaNutritionService"]

    constructor($scope: ng.IScope, public svc: INutritionService) {
        super($scope)
    }

    $onInit() {

        this.$scope.$watch("vm.app.stock", (newValue, oldValue) => {

            if (newValue != oldValue) {
                this.synchronizeAllRates()
            }

        })

    }

    protected setup() {

        const args: IEditPlanCroppingArguments = this.execArguments

        const ctxt = this.ctxt = args.context

        const cropping = ctxt.getCropping()

        this.groups = CroppingGroup.createGroups(cropping)

        this.$scope.$watch("vm.ctxt.biomass", () => this.synchronize(false))

    }

    get app(): ProductApplicationVm {
        if (!this.ctxt) return null
        let apps = this.ctxt.applications
        if (!apps) return null
        if (apps.length < 1) return null
        return apps[0]
    }

    hasValidApp(): boolean {
        let app = this.app
        return app && app.isValid()
    }

    isUsingNutrientRate(app: ProductApplicationVm): boolean {
        return app && (app.lastInputUsed == ProductAppInput.NutrientRate)
    }

    get useNutrientRate(): boolean {
        return this.isUsingNutrientRate(this.app)
    }

    set useNutrientRate(value: boolean) {
        let app = this.app
        if (app) {
            app.lastInputUsed = (value ? ProductAppInput.NutrientRate : ProductAppInput.ProductRate)
        }
    }

    get useBiomass(): boolean {
        return this.ctxt && this.ctxt.useBiomass
    }

    set useBiomass(value: boolean) {
        if (this.ctxt) {
            this.ctxt.useBiomass = value
        }
    }

    get canUseBiomass(): boolean {
        return this.ctxt && this.ctxt.canUseBiomass
    }

    get biomassOptions(): INutritionBiomassOptions {
        return this.ctxt ? this.ctxt.biomassOptions : null
    }

    get productRate(): number {
        let app = this.app
        return app ? app.productRate : null
    }

    set productRate(value: number) {
        let app = this.app
        if (app) app.productRate = value
    }

    get nutrientRate(): number {
        let app = this.app
        return app ? app.nutrientRate : null
    }

    set nutrientRate(value: number) {
        let app = this.app
        if (app) app.nutrientRate = value
    }

    get editCroppingLevelRates(): boolean {
        return this.groups && this.groups.some(g => g.cropping.some(c => c.selected && (!c.biomassLayer)))
    }

    get avgUnit() {
        const unit = getProductUnitsTextNormalized(this.app)
        return unit + "/ha"
    }

    get unit() {
        return getProductUnitsTextNormalized(this.app)
    }

    synchronize(updateApp: boolean = true) {
        this.recalculate(updateApp)
    }

    private synchronizeAllRates() {

        if (!this.app) return

        let crops = this.groups ? selectMany(this.groups.map(g => g.cropping.filter(c => c.selected && c.biomassAppRate))) : []

        const providers = [o => o.minRate, o => o.maxRate].concat(crops.map(c => o => c.biomassAppRate))

        let useNutrientRate = this.useNutrientRate

        this.updateAppRate(providers, useNutrientRate)

    }

    updateAppRate(providers: ((options: INutritionBiomassOptions) => INutritionAppRate)[], nutrient: boolean) {

        const { app, biomassOptions: options } = this

        if (!app) return
        if (!options) return

        providers.map(provider => provider(options)).filter(rate => !!rate).forEach(rate => {

            if (nutrient) {
                rate.Product = app.getProductRateForNutrientRate(rate.Nutrient)
            }
            else {
                rate.Nutrient = app.getNutrientRateForProductRate(rate.Product)
            }

        })

        this.synchronize()

    }

    updateCroppingAppRate(cropping: CroppingEditVm, nutrient: boolean) {
        this.updateAppRate([o => cropping.biomassAppRate], nutrient)
    }

    updateMinRate(nutrient: boolean) {
        this.updateAppRate([o => o.MinRate], nutrient)
    }

    updateMaxRate(nutrient: boolean) {
        this.updateAppRate([o => o.MaxRate], nutrient)
    }

    recalculate(updateApp: boolean = true) {

        const { app, biomassOptions: options, useNutrientRate } = this

        if (!app) return
        if (!options) return
        if (options.Auto) return

        const cropping = this.ctxt.cropping.filter(c => c.selected && c.biomassLayer)

        cropping.forEach(c => {

            let rate: number

            let minRate = useNutrientRate ? options.MinRate.Nutrient : options.MinRate.Product
            let maxRate = useNutrientRate ? options.MaxRate.Nutrient : options.MaxRate.Product

            rate = calculateCroppingAppRateManual(c.biomassLayer, minRate, maxRate, options.Rounding)

            let nutrientRate = useNutrientRate ? rate : app.getNutrientRateForProductRate(rate)
            let productRate = useNutrientRate ? app.getProductRateForNutrientRate(rate) : rate

            c.biomassAppRate = {
                Nutrient: nutrientRate,
                Product: productRate
            }

        })

        this.showApproxWarning = true

        if (updateApp && cropping.length) {

            let init = {
                area: 0,
                nutrient: 0,
                product: 0
            }

            let total = cropping.map(c => {
                return {
                    area: c.cropping.Area,
                    nutrient: c.biomassAppRate.Nutrient,
                    product: c.biomassAppRate.Product
                }
            }).reduce((p, c) => {
                return {
                    area: p.area + c.area,
                    nutrient: p.nutrient + (c.area * c.nutrient),
                    product: p.product + (c.area * c.product),
                }
            }, init)

            if (total.area) {

                const rnd = (v: number) => {
                    const r = Math.round(v / total.area)
                    return isNaN(r) ? undefined : r
                }

                if (useNutrientRate) {
                    app.nutrientRate = rnd(total.nutrient)
                }
                else {
                    app.productRate = rnd(total.product)
                }

            }

        }
    }
}
